/**
 * Web-specific tool executors for the AI assistant.
 *
 * These bridge the assistant's tool calls (which use desktop-style tab IDs)
 * to React Router navigation and shared Zustand stores.
 *
 * The `navigateRef` pattern lets executors (which run outside React component
 * context) access the router's navigate function. AppLayout populates the ref
 * on mount.
 *
 * Navigation tools are executed after the inline assistant tool confirmation.
 * The main chat session id is tracked via `mainChatSessionIdRef` so AppLayout
 * can open Gauss on the destination page with the same conversation.
 */

import { registerToolExecutors } from "@bastion/assistant";
import { useEpisodeDisplayStore, useEpisodeFiltersStore } from "@bastion/stores";
import { applyEpisodeFilterToolArgs } from "@bastion/episodes";
import {
  encodeFilterStateToParams,
  type ActiveFilter,
  type FilterState,
} from "@bastion/list-view";
import {
  WORKFLOW_FILTER_IDS,
  applyWorkflowTabToFilters,
  type WorkflowTabId,
} from "@bastion/ml-studio";
import { matchProductGroup } from "./navigation";

// ---------------------------------------------------------------------------
// Navigate ref — populated by AppLayout so executors can use React Router
// ---------------------------------------------------------------------------

type NavigateFn = (path: string) => void;
export type AssistantNavigationRequest = {
  targetPath: string;
  title: string;
  sessionIdToHandoff: string | null;
  afterNavigate?: () => void;
};

export const navigateRef: { current: NavigateFn | null } = { current: null };
export const assistantNavigationRef: {
  current: ((request: AssistantNavigationRequest) => void) | null;
} = { current: null };

/** Active `/chat/:sessionId` session on the main canvas (synced from WebAssistantWorkspace). */
export const mainChatSessionIdRef: { current: string | null } = { current: null };

function runNavigation(payload: {
  targetPath: string;
  title: string;
  afterNavigate?: () => void;
}): void {
  const request: AssistantNavigationRequest = {
    targetPath: payload.targetPath,
    title: payload.title,
    sessionIdToHandoff: mainChatSessionIdRef.current,
    afterNavigate: payload.afterNavigate,
  };
  if (assistantNavigationRef.current) {
    assistantNavigationRef.current(request);
    return;
  }
  if (navigateRef.current) {
    navigateRef.current(request.targetPath);
    request.afterNavigate?.();
  } else {
    console.warn("[assistantTools] navigateRef not set — cannot navigate to", request.targetPath);
  }
}

// ---------------------------------------------------------------------------
// Tab ID → web route mapping
// ---------------------------------------------------------------------------

const TAB_TO_ROUTE: Record<string, string> = {
  // Product areas (web)
  chat: "/",
  home: "/",
  "data-studio": "/episodes",
  episodes: "/episodes",
  datasets: "/datasets",
  "collector-insights": "/collector-insights",
  dashboards: "/dashboards",
  "ml-studio": "/ml-studio/workflows",
  analytics: "/dashboards",
  fleet: "/telemetry",
  telemetry: "/telemetry",
  devservers: "/devservers",
  settings: "/settings",
  design: "/design",
  // Desktop-only tabs → fallback to home
  "collector-record": "/",
  evaluator: "/",
};

const TAB_TITLE: Record<string, string> = {
  chat: "Chat home",
  home: "Chat home",
  "data-studio": "Episodes",
  episodes: "Episodes",
  datasets: "Datasets",
  "ml-studio": "ML",
  analytics: "Dashboards",
  dashboards: "Dashboards",
  "collector-insights": "Collector insights",
  fleet: "Telemetry",
  telemetry: "Telemetry",
  devservers: "Dev servers",
  settings: "Settings",
  design: "Design system",
  "collector-record": "Home",
  evaluator: "Home",
};

function titleForTab(tabId: string): string {
  return TAB_TITLE[tabId] ?? tabId.replace(/-/g, " ");
}

function stringArg(args: Record<string, unknown>, key: string): string {
  const raw = args[key];
  return typeof raw === "string" ? raw.trim() : "";
}

// ---------------------------------------------------------------------------
// Route → tab ID mapping (for getAppState)
// ---------------------------------------------------------------------------

/**
 * Derive a product / tab-style identifier from the current URL pathname
 * for assistant app state (aligned with navigate_to_tab tab_id values).
 */
export function routeToTabId(pathname: string): string {
  if (pathname === "/design" || pathname.startsWith("/design/")) {
    return "design";
  }
  return matchProductGroup(pathname);
}

// ---------------------------------------------------------------------------
// Executor functions
// ---------------------------------------------------------------------------

function executeNavigateToTab(args: Record<string, unknown>): void {
  const raw = args.tab_id;
  const tabId = stringArg(args, "tab_id");
  const route = tabId ? TAB_TO_ROUTE[tabId] : undefined;
  if (!route) {
    console.warn("[assistantTools] navigate_to_tab: unknown or missing tab_id", raw);
    return;
  }
  runNavigation({ targetPath: route, title: titleForTab(tabId) });
}

function executeNavigateToDataset(args: Record<string, unknown>): void {
  const datasetId = stringArg(args, "dataset_id");
  if (!datasetId) {
    console.warn("[assistantTools] navigate_to_dataset: missing dataset_id", args.dataset_id);
    return;
  }
  runNavigation({
    targetPath: `/datasets/${datasetId}`,
    title: "Dataset",
  });
}

function executeNavigateToWorkflowRun(args: Record<string, unknown>): void {
  const workflowRunId = stringArg(args, "workflow_run_id");
  if (!workflowRunId) {
    console.warn(
      "[assistantTools] navigate_to_workflow_run: missing workflow_run_id",
      args.workflow_run_id,
    );
    return;
  }
  runNavigation({
    targetPath: `/ml-studio/workflows/${workflowRunId}`,
    title: "Workflow run",
  });
}

function executeNavigateToExperiment(args: Record<string, unknown>): void {
  executeNavigateToWorkflowRun({ workflow_run_id: args.experiment_id });
}

function executeSetEpisodeFilters(args: Record<string, unknown>): void {
  runNavigation({
    targetPath: "/episodes",
    title: "Episodes",
    afterNavigate: () => {
      const filterStore = useEpisodeFiltersStore.getState();
      const displaySnap = {
        viewMode: useEpisodeDisplayStore.getState().viewMode,
        sort: useEpisodeDisplayStore.getState().sort,
        pageSize: useEpisodeDisplayStore.getState().pageSize,
      };
      const { filters, display } = applyEpisodeFilterToolArgs(filterStore.filters, displaySnap, args);
      filterStore.setFilters(filters);
      if (display) {
        useEpisodeDisplayStore.getState().patch(display);
      }
    },
  });
}

function executeClearEpisodeFilters(): void {
  runNavigation({
    targetPath: "/episodes",
    title: "Episodes",
    afterNavigate: () => {
      useEpisodeFiltersStore.getState().resetFilters();
      useEpisodeDisplayStore.getState().reset();
    },
  });
}

// ---------------------------------------------------------------------------
// Workflow filters
// ---------------------------------------------------------------------------

/** Convert a string-or-string-array tool arg into a clean string[]. */
function toValueArray(value: unknown): string[] {
  if (value == null) return [];
  const items = Array.isArray(value) ? value : [value];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    if (typeof item !== "string") continue;
    const trimmed = item.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

function isWorkflowTab(value: unknown): value is WorkflowTabId {
  return value === "active" || value === "completed" || value === "all";
}

/**
 * Build the workflow filter URL from a `set_workflow_filters` tool args dict.
 * Returns an absolute-style path `/ml-studio/workflows[?…]`.
 *
 * The `tab` arg (or legacy `preset`) is treated as a status-chip shortcut
 * and folded into the filter state alongside any explicit `status`. Other
 * known fields land as their own chips.
 */
export function buildWorkflowFiltersTargetPath(
  args: Record<string, unknown>,
): string {
  const filters: ActiveFilter[] = [];
  const statusValues = toValueArray(args.status);
  if (statusValues.length > 0) {
    filters.push({ attributeId: "status", operator: "is", values: statusValues });
  }
  const tagValues = toValueArray(args.tag);
  if (tagValues.length > 0) {
    filters.push({ attributeId: "tag", operator: "is", values: tagValues });
  }
  const workflowNameValues = toValueArray(args.workflow_name);
  if (workflowNameValues.length > 0) {
    filters.push({
      attributeId: "workflow_name",
      operator: "is",
      values: workflowNameValues,
    });
  }

  let filterState: FilterState = { filters };
  const tabRaw = args.tab ?? args.preset;
  if (isWorkflowTab(tabRaw) && tabRaw !== "all" && statusValues.length === 0) {
    filterState = applyWorkflowTabToFilters(filterState, tabRaw);
  }

  const params = new URLSearchParams();
  encodeFilterStateToParams(filterState, params, WORKFLOW_FILTER_IDS);

  const query = params.toString();
  return query.length > 0
    ? `/ml-studio/workflows?${query}`
    : "/ml-studio/workflows";
}

function executeSetWorkflowFilters(args: Record<string, unknown>): void {
  runNavigation({
    targetPath: buildWorkflowFiltersTargetPath(args),
    title: "Workflows",
  });
}

// ---------------------------------------------------------------------------
// Registration (call once from AppLayout)
// ---------------------------------------------------------------------------

let registered = false;

export function registerWebToolExecutors(): void {
  if (registered) return;
  registered = true;

  registerToolExecutors({
    navigate_to_tab: executeNavigateToTab,
    navigate_to_dataset: executeNavigateToDataset,
    navigate_to_experiment: executeNavigateToExperiment,
    navigate_to_workflow_run: executeNavigateToWorkflowRun,
    set_episode_filters: executeSetEpisodeFilters,
    clear_episode_filters: executeClearEpisodeFilters,
    set_workflow_filters: executeSetWorkflowFilters,
    // set_collection_task and set_collection_presets are desktop-only —
    // the server will exclude them for web clients (step 3).
  });
}
